<template>
  <div :class="getClassName">
    <InputValidator
      :config="{
        dataLpignore: true,
        errorStyleExceptions: getErrorExceptionStyle,
        name: field,
        placeholder: placeholder,
        readonly: !isSelected || showReadOnly,
        validate: validation,
        value: item[field],
      }"
      :name="field"
      @InputValidatorChange="handleInputOnBlur"
      @InputValidatorFocus="handleInputOnFocus"
      @InputValidatorModel="handleInputOnChange"
    />
  </div>
</template>

<script>
import { extend } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';

export default {
  name: 'EditableInputValidator',

  components: {
    InputValidator,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    field: {
      type: String,
      default: '',
    },
    hideErrorValidation: {
      type: Boolean,
      default: false,
    },
    item: {
      required: true,
      type: Object,
    },
    placeholder: {
      type: String,
      default: '',
    },
    showNarrowInput: {
      type: Boolean,
      default: false,
    },
    showReadOnly: {
      type: Boolean,
      default: false,
    },
    storeModule: {
      type: String,
      default: 'ImportUsersCollection',
    },
    updateItemErrorHandler: {
      default: () => {},
      type: Function,
    },
    validateDuplicates: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      errorNotified: false,
    };
  },

  computed: {
    getClassName() {
      return this.showNarrowInput
        ? this.$style.textInputNarrow
        : this.$style.textInput;
    },

    getErrorExceptionStyle() {
      return this.hideErrorValidation
        ? this.$style.hidden
        : this.$style.error;
    },

    isSelected() {
      return this.selectedItems.indexOf(this.item.id) !== -1;
    },
  },

  watch: {
    isSelected(newValue) {
      const errorObject = { selectedItemId: this.item.id, fieldName: this.field };

      if (newValue === true) {
        this.$validator.validateAll().then((valid) => {
          if (!valid) {
            this.addValidationError(errorObject);
          }
        });
      } else if (this.$children[0].$validator.errors.items.length > 0) {
        this.removeValidationError(errorObject);
      }

      this.$children[0].$validator.errors.clear();
    },
  },

  beforeCreate() {
    const storeModule = this.$options.propsData.storeModule
      || this.$options.props.storeModule.default;

    extend(
      this.$options.computed,
      mapGetters(storeModule, {
        selectedItems: 'getSelectedItems',
        validationErrors: 'getValidationErrors',
      }),
    );

    extend(
      this.$options.methods,
      mapActions(storeModule, [
        'addDuplicateValidationError',
        'addValidationError',
        'removeDuplicateValidationError',
        'removeValidationError',
        'selectItem',
        'updateItem',
      ]),
    );
  },

  methods: {
    /**
     * Checks if there are no validation errors and -if not- calls `updateItemWrapper`.
     *
     * *Executed on `blur`.*
     * @param {string} value Input's value
     */
    async handleInputOnBlur(value) {
      const hasErrors = this.validationErrors.length > 0
        && this.validationErrors.some(i => i.selectedItemId === this.item.id);

      if (!hasErrors) {
        this.updateItemWrapper({ id: this.item.id, key: this.field, value });
      }
    },

    /**
     * Catches any errors that are related to the input value's format.
     *
     * *Executed on `change`.*
     */
    async handleInputOnChange() {
      const valid = await this.$validator.validate(this.field);
      const errorObject = { selectedItemId: this.item.id, fieldName: this.field };

      if (!valid) {
        this.addValidationError(errorObject);
      } else {
        this.removeValidationError(errorObject);
      }
    },

    /**
     * Adds the current item to the store if the input is enabled.
     *
     * - *Executed on `focus`*
     */
    handleInputOnFocus() {
      if (this.disabled) {
        return;
      }

      this.selectItem({ id: this.item.id });
    },

    /**
     * Executes the request to modify the item.
     *
     * Also, if `validateDuplicates` is `true` and the input value is duplicate,
     * it will trigger an alert.
     * @param {object} payload Contains `id`, `key` and `value` of the current item.
     */
    async updateItemWrapper(payload) {
      const error = await this.updateItem(payload).catch(e => e);
      const { id, key } = payload;
      const errorObject = { selectedItemId: id, fieldName: key };

      if (error) {
        if (error.message === 'Already Exists' && this.validateDuplicates) {
          this.addDuplicateValidationError(errorObject);
        }

        this.updateItemErrorHandler(error, payload);
      } else if (this.validateDuplicates) {
        this.removeDuplicateValidationError(errorObject);
      }
    },
  },
};
</script>

<style module>
.error {
  color: var(--jc-color-error-base);
  font-size: var(--jcBody);
  font-weight: normal;
  line-height: 1.33em;
  min-height: 1.33em;
  position: relative;
}

.hidden {
  display: none;
}

.textInput {
  composes: textInput from '~client/directories/Workday/Workday.css';
  margin-bottom: var(--jc-spacer-x-small);
  padding: 0;
}

.textInput input[readonly] {
  padding-left: 0;
}

.textInputNarrow {
  composes: textInput;
  width: 60%;
}
</style>
